import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { mediaSizeHint, type Category, type FaqItem, type Media, type SiteSettings, type SlugSpace } from '@nhanho/shared'
import { categories as api, products as productsApi, settings as settingsApi, explainError, type CategoryNode } from '../../lib/api'
import { useToast } from '../../lib/toast'
import { PageHeader } from '../../components/ui/PageHeader'
import { Button } from '../../components/ui/Button'
import { Input, Select } from '../../components/ui/Input'
import { Field } from '../../components/ui/Field'
import { Toggle } from '../../components/ui/Toggle'
import { Badge } from '../../components/ui/Badge'
import { Modal, ConfirmDialog } from '../../components/ui/Modal'
import { Spinner } from '../../components/ui/Spinner'
import { EmptyState } from '../../components/ui/EmptyState'
import { ImageField } from '../../components/media/ImageField'
import { RichTextEditor } from '../../components/editor/RichTextEditor'
import { RichTextPreview } from '../../components/editor/RichTextPreview'
import { SeoPanel, EMPTY_SEO, seoFromMeta, seoToMeta, type SeoValue } from '../../components/seo/SeoPanel'
import { FaqEditor } from '../../components/seo/FaqEditor'
import { Card } from '../../components/ui/Card'

interface Form {
  space: SlugSpace
  name: string; slug: string; description: string; parent: string; published: boolean; image: Media | null
  showOnHomepage: boolean
  longContentTitle: string; longContent: string
  faq: FaqItem[]; seo: SeoValue
}
const EMPTY: Form = {
  space: 'product',
  name: '', slug: '', description: '', parent: '', published: true, image: null,
  showOnHomepage: true,
  longContentTitle: '', longContent: '',
  faq: [], seo: EMPTY_SEO,
}

/** Tiền tố URL theo không gian — hiện trong gợi ý slug và hộp thoại cảnh báo. */
const PREFIX: Record<SlugSpace, string> = { product: '/san-pham', post: '/blog' }
const SPACE_LABEL: Record<SlugSpace, string> = { product: 'Sản phẩm', post: 'Blog' }

type Tab = 'basic' | 'long' | 'seo'
const TABS: { k: Tab; label: string }[] = [
  { k: 'basic', label: 'Thông tin' },
  { k: 'long', label: 'Nội dung dài' },
  { k: 'seo', label: 'SEO' },
]
/** Tab chứa ô lỗi đầu tiên — nhảy tới đúng chỗ thay vì báo "có ô đỏ" ở tab đang khuất. */
const firstErrorTab = (fe: Record<string, string>): Tab => {
  const keys = Object.keys(fe)
  if (keys.some((k) => k.startsWith('meta') || k.startsWith('faq'))) return 'seo'
  if (keys.some((k) => k.startsWith('longContent'))) return 'long'
  return 'basic'
}

/** Phẳng hoá cây để làm option "danh mục cha", có thụt lề theo cấp. */
function flatten(nodes: CategoryNode[], depth = 0, out: { id: string; label: string; depth: number }[] = []) {
  for (const n of nodes) { out.push({ id: n.id, label: n.name, depth }); flatten(n.children, depth + 1, out) }
  return out
}
/** Mọi id con cháu của một node — để không cho chọn chính con mình làm cha. */
function descendants(node: CategoryNode, out = new Set<string>()) {
  for (const c of node.children) { out.add(c.id); descendants(c, out) }
  return out
}
function findNode(nodes: CategoryNode[], id: string): CategoryNode | null {
  for (const n of nodes) { if (n.id === id) return n; const f = findNode(n.children, id); if (f) return f }
  return null
}

export default function CategoryTree() {
  const toast = useToast()
  /**
   * Không gian đang xem. Một collection phục vụ hai cây (danh mục sản phẩm và danh mục
   * blog) từ 2026-09-11 — tách tab chứ không tách trang, vì mọi thao tác y hệt nhau.
   */
  const [space, setSpace] = useState<SlugSpace>('product')
  const [tree, setTree] = useState<CategoryNode[] | null>(null)
  /** Slug lúc mở form — để biết có đổi hay không mà cảnh báo. */
  const [initialSlug, setInitialSlug] = useState('')
  const [initialPublished, setInitialPublished] = useState(false)
  const [confirmSlug, setConfirmSlug] = useState<null | (() => void)>(null)
  const [editing, setEditing] = useState<string | 'new' | null>(null)
  const [form, setForm] = useState<Form>(EMPTY)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)
  const [del, setDel] = useState<CategoryNode | null>(null)
  const [delBlocked, setDelBlocked] = useState<string | null>(null)
  const [moving, setMoving] = useState(false)
  const [tab, setTab] = useState<Tab>('basic')
  /**
   * Tab SEO **mount trễ rồi ở lại**. Mount ngay từ đầu thì mỗi lần mở modal là một
   * request `/seo/conflicts` dù người dùng không định đụng SEO; unmount khi rời tab thì
   * mất chữ đang gõ dở. Cờ này cho cả hai: chưa vào thì chưa dựng, vào rồi thì giữ.
   */
  const [seoVisited, setSeoVisited] = useState(false)
  const [preview, setPreview] = useState(false)
  /** Cài đặt SEO toàn site — `SeoPanel` cần để dựng mẫu tiêu đề và cảnh báo chặn index. */
  const [site, setSite] = useState<SiteSettings | null>(null)
  /** Số sản phẩm thuộc danh mục đang sửa (tính cả nhánh con) — đầu vào chấm điểm SEO. */
  const [productCount, setProductCount] = useState(0)
  const [sp, setSp] = useSearchParams()

  useEffect(() => { settingsApi.get().then(setSite).catch(() => setSite(null)) }, [])

  const load = useCallback(
    () => api.tree(space).then((r) => setTree(r.tree)).catch((e) => toast.error(explainError(e).message)),
    [toast, space],
  )
  useEffect(() => { void load() }, [load])

  const openNew = (parent = '') => {
    setEditing('new'); setForm({ ...EMPTY, space, parent }); setErrors({})
    setInitialSlug(''); setInitialPublished(false)
    setTab('basic'); setSeoVisited(false); setPreview(false); setProductCount(0)
  }
  const openEdit = async (id: string) => {
    // Không có try/catch thì lỗi 404/500 chỉ làm modal không mở, không báo gì.
    try {
      const c: Category = await api.get(id)
      setEditing(id)
      setInitialSlug(c.slug)
      setInitialPublished(c.published)
      setForm({
        space: c.space ?? 'product',
        name: c.name, slug: c.slug, description: c.description ?? '', published: c.published,
        showOnHomepage: c.showOnHomepage !== false,
        parent: typeof c.parent === 'object' && c.parent ? (c.parent as Category).id : (c.parent as string | null) ?? '',
        image: typeof c.image === 'object' ? (c.image as Media | null) : null,
        longContentTitle: c.longContentTitle ?? '', longContent: c.longContent ?? '',
        faq: c.faq ?? [], seo: seoFromMeta(c.meta),
      })
      setErrors({})
      setTab('basic')
      setSeoVisited(false)
      setPreview(false)
      // `limit: 0` = chỉ cần `totalDocs`, không kéo document nào về.
      productsApi.list({ categorySlug: c.slug, limit: 0, published: 'all' })
        .then((r) => setProductCount(r.totalDocs))
        .catch(() => setProductCount(0))
    } catch (e) {
      toast.error(`Không mở được danh mục: ${explainError(e).message}`)
    }
  }

  /**
   * Đổi slug của danh mục **đang hiển thị** = link cũ chết. Trước 2026-09-11 route này là
   * chỗ duy nhất đổi slug mà không hỏi gì cả. Giờ hỏi, và server tự ghi 301.
   */
  const save = () => {
    if (editing && editing !== 'new' && initialPublished && form.slug && form.slug !== initialSlug) {
      setConfirmSlug(() => () => void doSave())
      return
    }
    void doSave()
  }

  const doSave = async () => {
    setBusy(true); setErrors({})
    const body = {
      space: form.space,
      name: form.name, slug: form.slug || undefined, description: form.description,
      published: form.published, parent: form.parent || null, image: form.image?.id ?? null,
      showOnHomepage: form.showOnHomepage,
      longContentTitle: form.longContentTitle, longContent: form.longContent,
      faq: form.faq.filter((x) => x.question.trim() && x.answer.trim()),
      meta: seoToMeta(form.seo),
    }
    try {
      if (editing === 'new') await api.create(body); else if (editing) await api.update(editing, body)
      const renamed = editing !== 'new' && initialPublished && form.slug !== initialSlug
      toast.success(renamed ? 'Đã lưu — đã tạo điều hướng 301 từ link cũ' : 'Đã lưu danh mục')
      setConfirmSlug(null); setEditing(null); await load()
    } catch (e) {
      // Lỗi theo field thì tô đỏ đúng ô; lỗi chung (409 trùng, 422 vòng lặp cha-con,
      // mất mạng) thì toast — trước đây mọi thứ bị nhồi xuống ô "Tên".
      const { message, fieldErrors } = explainError(e)
      setErrors(fieldErrors)
      const next = firstErrorTab(fieldErrors)
      setTab(next)
      if (next === 'seo') setSeoVisited(true)
      toast.error(message)
    } finally { setBusy(false) }
  }

  const remove = async () => {
    if (!del) return
    setBusy(true); setDelBlocked(null)
    try { await api.remove(del.id); toast.success('Đã xoá'); setDel(null); await load() }
    catch (e) {
      // 409 = còn con hoặc còn sản phẩm. Hiện NGAY TRONG hộp thoại (như MediaLibrary),
      // không để dialog mở với nút Xoá vẫn sẵn sàng và lý do bay theo toast sau 6s.
      const { message, status } = explainError(e)
      if (status === 409) setDelBlocked(message)
      else toast.error(message)
    } finally { setBusy(false) }
  }

  /** Đổi thứ tự trong cùng cấp: lên/xuống một bậc, ghi lại `order` cho cả nhóm anh em. */
  const move = async (siblings: CategoryNode[], idx: number, dir: -1 | 1) => {
    const to = idx + dir
    if (to < 0 || to >= siblings.length || moving) return
    const next = [...siblings]; const [it] = next.splice(idx, 1); next.splice(to, 0, it!)
    setMoving(true)
    try {
      await api.reorder(next.map((n, i) => ({ id: n.id, order: i })))
      await load()
    } catch (e) {
      toast.error(`Không đổi được thứ tự: ${explainError(e).message}`)
    } finally { setMoving(false) }
  }

  /**
   * `?edit=<id>` mở sẵn modal — trang "Sức khoẻ SEO" cần chỗ để bấm tới, mà danh mục
   * không có route riêng như sản phẩm/bài viết. Xoá param ngay để F5 không mở lại.
   */
  useEffect(() => {
    const id = sp.get('edit')
    if (!id || editing) return
    void openEdit(id)
    const next = new URLSearchParams(sp)
    next.delete('edit')
    setSp(next, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sp])

  const editingNode = editing && editing !== 'new' && tree ? findNode(tree, editing) : null
  const blocked = editingNode ? new Set([editingNode.id, ...descendants(editingNode)]) : new Set<string>()

  const render = (nodes: CategoryNode[], depth = 0) => (
    <ul className={depth ? 'ml-6 border-l border-border pl-3' : ''}>
      {nodes.map((n, i) => (
        <li key={n.id} className="py-1">
          <div className="group flex items-center gap-2 rounded-button px-2 py-1.5 hover:bg-sand">
            <div className="size-8 shrink-0 overflow-hidden rounded-image bg-primary-50">{n.image && <img src={n.image.sizes?.thumbnail?.url ?? n.image.url} alt="" className="size-full object-cover" />}</div>
            <button type="button" onClick={() => void openEdit(n.id)} className="min-w-0 flex-1 text-left text-sm font-medium text-ink hover:underline">{n.name} <span className="ml-1 text-xs font-normal text-ink-faint">/{n.slug}</span></button>
            {!n.published && <Badge>Ẩn</Badge>}
            {space === 'product' && !n.showOnHomepage && <Badge>Ngoài trang chủ</Badge>}
            {n.children.length > 0 && <Badge tone="primary">{n.children.length} con</Badge>}
            <div className="hidden items-center gap-0.5 group-hover:flex">
              <Button variant="ghost" size="sm" disabled={i === 0 || moving} onClick={() => void move(nodes, i, -1)} aria-label="Lên">↑</Button>
              <Button variant="ghost" size="sm" disabled={i === nodes.length - 1 || moving} onClick={() => void move(nodes, i, 1)} aria-label="Xuống">↓</Button>
              <Button variant="ghost" size="sm" onClick={() => openNew(n.id)}>+ Con</Button>
              <Button variant="ghost" size="sm" className="text-danger" onClick={() => setDel(n)}>Xoá</Button>
            </div>
          </div>
          {n.children.length > 0 && render(n.children, depth + 1)}
        </li>
      ))}
    </ul>
  )

  return (
    <>
      <PageHeader
        title="Danh mục"
        subtitle={`Cây phân cấp: cha → con. Trang đích ${PREFIX[space]}/<slug> — cây không nằm trong đường dẫn, chỉ để tổ chức và lọc.`}
        actions={<Button onClick={() => openNew()}>+ Danh mục gốc</Button>}
      />

      {/**
        * Hai không gian, một collection. Tách tab thay vì tách trang vì mọi thao tác —
        * cây, ảnh, SEO, nội dung dài — giống hệt nhau; hai trang là hai bản sao phải
        * sửa song song mãi mãi.
        */}
      <div className="mb-4 flex gap-1 border-b border-border">
        {(['product', 'post'] as const).map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => { setSpace(k); setTree(null) }}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
              space === k ? 'border-primary-700 text-primary-700' : 'border-transparent text-ink-muted hover:text-ink'
            }`}
          >
            {SPACE_LABEL[k]}
          </button>
        ))}
      </div>

      {!tree ? <Spinner /> : tree.length === 0 ? (
        <EmptyState
          title={space === 'product' ? 'Chưa có danh mục sản phẩm' : 'Chưa có danh mục blog'}
          hint={space === 'product' ? 'Ví dụ: "Móc khóa" → con "Móc khóa cáo".' : 'Ví dụ: "Mẹo in 3D", "Chất liệu".'}
          action={<Button onClick={() => openNew()}>+ Danh mục gốc</Button>}
        />
      ) : <div className="rounded-card border border-border bg-white p-3 shadow-card">{render(tree)}</div>}

      {/**
        * `wide` là bắt buộc, không phải thẩm mỹ: `SeoPanel` root là
        * `grid lg:grid-cols-[1fr_340px]`, mà `lg:` là media query theo **viewport** chứ
        * không theo container — trong hộp `max-w-lg` (512px) nó không xuống 1 cột mà bóp
        * cột trái còn ~100px.
        */}
      <Modal wide open={editing !== null} onClose={() => setEditing(null)} title={editing === 'new' ? 'Thêm danh mục' : 'Sửa danh mục'}
        footer={<><Button variant="secondary" onClick={() => setEditing(null)}>Huỷ</Button><Button onClick={save} loading={busy}>Lưu</Button></>}>
        <div className="mb-4 flex gap-1 border-b border-border">
          {TABS.map((t) => (
            <button key={t.k} type="button" onClick={() => { setTab(t.k); if (t.k === 'seo') setSeoVisited(true) }}
              className={`-mb-px flex items-center gap-1.5 border-b-2 px-4 py-2 text-sm font-medium ${tab === t.k ? 'border-primary-700 text-primary-950' : 'border-transparent text-ink-muted hover:text-ink'}`}>
              {t.label}
              {Object.keys(errors).length > 0 && firstErrorTab(errors) === t.k && <span className="size-2 rounded-full bg-danger" aria-label="Có lỗi" />}
            </button>
          ))}
        </div>

        <div className={tab === 'basic' ? 'space-y-4' : 'hidden'}>
          <Field label="Tên" required error={errors.name}><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus /></Field>
          <Field
            label="Slug"
            hint={initialPublished
              ? `Đang hiển thị — đổi slug sẽ tạo điều hướng 301 từ ${PREFIX[form.space]}/${initialSlug}`
              : `${PREFIX[form.space]}/<slug>. Để trống sẽ tự sinh từ tên.`}
            error={errors.slug}
          >
            <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} />
          </Field>
          <Field label="Danh mục cha" error={errors.parent}>
            <Select value={form.parent} onChange={(e) => setForm({ ...form, parent: e.target.value })}>
              <option value="">— Là danh mục gốc —</option>
              {tree && flatten(tree).filter((o) => !blocked.has(o.id)).map((o) => <option key={o.id} value={o.id}>{'  '.repeat(o.depth)}{o.depth ? '└ ' : ''}{o.label}</option>)}
            </Select>
          </Field>
          <Field as="div" label="Ảnh đại diện" hint={mediaSizeHint()} error={errors.image}><ImageField value={form.image} onChange={(m) => setForm({ ...form, image: m })} /></Field>
          <Field as="div" label="Mô tả ngắn" hint="Hiện ở đầu trang /danh-muc/<slug>, cạnh ảnh. Vài câu là đủ — phần dài viết ở tab &quot;Nội dung dài&quot;." error={errors.description}>
            <RichTextEditor content={form.description} onChange={(html) => setForm({ ...form, description: html })} resetKey={editing ?? 'new'} placeholder="Danh mục này gồm những gì, hợp với ai…" />
          </Field>
          <Toggle checked={form.published} onChange={(v) => setForm({ ...form, published: v })} label="Hiển thị trên website" />
          {/**
            * Cờ thứ hai nhưng **không** phải cờ ẩn/hiện: nó chỉ quyết định có ô ở dải danh mục
            * trang chủ hay không (xem `models/Category.ts`). Chỉ hiện với danh mục sản phẩm —
            * dải đó không lấy danh mục blog, để toggle ở tab Blog là hứa một việc không có.
            */}
          {form.space === 'product' && (
            <div>
              <Toggle checked={form.showOnHomepage} onChange={(v) => setForm({ ...form, showOnHomepage: v })} label="Hiện ở dải danh mục trang chủ" />
              <p className="mt-1 text-xs text-ink-muted">
                Tắt khi muốn trang chủ chỉ hiện các danh mục con. Không ảnh hưởng gì khác:{' '}
                <code>{PREFIX[form.space]}/{form.slug || '<slug>'}</code> vẫn vào được, vẫn trong
                sitemap, vẫn lọc được ở /san-pham và vẫn ở footer.
              </p>
            </div>
          )}
        </div>

        {/**
          * Ẩn bằng `hidden` chứ không unmount: gõ dở ở tab SEO rồi lỡ bấm sang tab kia mà
          * mất chữ là lỗi kinh điển. Đổi lại `SeoPanel` chạy ngay cả khi đang ở tab
          * "Thông tin" — chấp nhận được, nó chỉ là tính toán chuỗi thuần.
          */}
        {/* Ẩn bằng `hidden` chứ không unmount — đổi tab mà mất chữ đang gõ là lỗi kinh điển. */}
        <div className={tab === 'long' ? 'space-y-4' : 'hidden'}>
          <Field label="Tiêu đề khối" hint="Hiện thành thẻ H2 ngay trên nội dung. Để trống thì lấy tên danh mục." error={errors.longContentTitle}>
            <Input value={form.longContentTitle} onChange={(e) => setForm({ ...form, longContentTitle: e.target.value })} placeholder="Tổng hợp các mẫu móc khoá in 3D" />
          </Field>
          <Field as="div" label="Nội dung dài (khối SEO)" hint="Hiện dưới lưới sản phẩm, cắt bớt kèm nút Xem thêm. Dùng H2/H3 — trang đã có sẵn H1 là tên danh mục. Link trong site viết dạng /danh-muc/… để không bị nofollow." error={errors.longContent}>
            <RichTextEditor content={form.longContent} onChange={(html) => setForm({ ...form, longContent: html })} resetKey={editing ?? 'new'} placeholder="Viết bài giới thiệu nhóm sản phẩm này…" />
          </Field>
          <div>
            <Button variant="secondary" size="sm" onClick={() => setPreview((v) => !v)}>
              {preview ? 'Ẩn xem trước' : 'Xem trước'}
            </Button>
          </div>
          {preview && <RichTextPreview html={form.longContent} title={form.longContentTitle || form.name} />}
        </div>

        {seoVisited && (
        <div className={tab === 'seo' ? 'space-y-5' : 'hidden'}>
          <SeoPanel
            kind="category"
            value={form.seo}
            onChange={(seo) => setForm({ ...form, seo })}
            pageTitle={form.name}
            slug={`${PREFIX[form.space].slice(1)}/${form.slug}`}
            html={`${form.description}\n${form.longContent}`}
            errors={errors}
            settings={site}
            docId={editing && editing !== 'new' ? editing : undefined}
            extra={{
              attachedImages: form.image ? [{ alt: form.image.alt }] : [],
              productCount,
              childCount: editingNode?.children.length ?? 0,
              faqCount: form.faq.filter((f) => f.question.trim() && f.answer.trim()).length,
            }}
          />
          <Card title="Câu hỏi thường gặp (FAQ trên Google)">
            <FaqEditor items={form.faq} onChange={(faq) => setForm({ ...form, faq })} errors={errors} />
          </Card>
        </div>
        )}
      </Modal>
      <ConfirmDialog
        open={confirmSlug !== null}
        onCancel={() => setConfirmSlug(null)}
        onConfirm={() => confirmSlug?.()}
        loading={busy}
        confirmLabel="Vẫn đổi slug"
        title="Đổi slug của danh mục đang hiển thị?"
        message={<>
          Link cũ <code>{PREFIX[form.space]}/{initialSlug}</code> sẽ được <strong>tự động
          chuyển 301</strong> sang địa chỉ mới, nên không ai gặp 404. Nhưng Google vẫn mất
          một thời gian để chuyển thứ hạng sang URL mới. Nếu chỉ sửa tên hiển thị thì{' '}
          <strong>giữ nguyên slug</strong>.
        </>}
      />
      <ConfirmDialog open={!!del} onCancel={() => { setDel(null); setDelBlocked(null) }} onConfirm={remove} loading={busy} title="Xoá danh mục"
        message={delBlocked
          ? <p className="text-danger">{delBlocked}</p>
          : <>Xoá <strong>{del?.name}</strong>? Hệ thống sẽ từ chối nếu còn danh mục con hoặc sản phẩm thuộc danh mục này.</>} />
    </>
  )
}
